/* eslint-disable react/prop-types */
/* eslint-disable react/require-default-props */
import React, { useState, useMemo } from 'react';
import Table, { TableSize } from '@components/Table';
import { translate } from '@utils/i18n';
import { Button } from '@ui';
import TooltipText from '@components/v2/TooltipText';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { useRemoveQuestionFromSetMutation } from '../hooks/useQuestionSetQuestions';
const namespace = 'features.settings.component_questions';
function QuestionRenderer({ item }) {
    return (React.createElement("div", { className: "component-question-row" },
        React.createElement("div", { className: "component-question-info" },
            React.createElement("div", { className: "component-question-text" }, item.questionText))));
}
function QuestionTypeRenderer({ item }) {
    return React.createElement("span", null, item.questionType);
}
function FrequencyRenderer({ item }) {
    return React.createElement("span", null, item.frequencyType);
}
function SectionRenderer({ item }) {
    var _a;
    return React.createElement("span", null, ((_a = item.section) === null || _a === void 0 ? void 0 : _a.name) || '-');
}
function ActionsRenderer({ item, questionSetId, onRemove, deletingQuestionId }) {
    const deleteLabel = translate('delete', { namespace: 'generic' });
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const isDeleting = deletingQuestionId === item.id;
    const handleDeleteClick = () => {
        setIsDeleteConfirmOpen(true);
    };
    const handleConfirmDelete = () => {
        onRemove({ questionSetId, questionId: item.id });
        setIsDeleteConfirmOpen(false);
    };
    const handleCancelDelete = () => {
        setIsDeleteConfirmOpen(false);
    };
    return (React.createElement("div", { className: "component-actions-row" },
        React.createElement(TooltipText, { text: deleteLabel, position: "top" },
            React.createElement(Button, { "aria-label": `${deleteLabel} ${item.questionText}`, onClick: handleDeleteClick, disabled: isDeleting }, isDeleting ? (React.createElement("i", { className: "fa fa-spinner fa-spin", "aria-hidden": "true" })) : (React.createElement("i", { className: "fa-light fa-trash", "aria-hidden": "true" })))),
        isDeleteConfirmOpen && (React.createElement(ConfirmDialog, { title: translate('delete_question_title', { namespace }), description: translate('delete_question_description', { namespace }), onConfirm: handleConfirmDelete, onCancel: handleCancelDelete, yesLabel: translate('delete', { namespace: 'features.commons' }), noLabel: translate('cancel', { namespace: 'features.commons' }) }))));
}
export default function QuestionSetQuestionsTable({ questions, questionSetId, searchQuery = '' }) {
    const [deletingQuestionId, setDeletingQuestionId] = useState(null);
    const removeMutation = useRemoveQuestionFromSetMutation();
    const handleRemove = (params) => {
        setDeletingQuestionId(params.questionId);
        removeMutation.mutate(params, {
            onSettled: () => {
                setDeletingQuestionId(null);
            }
        });
    };
    // Filter questions based on search query
    const filteredQuestions = useMemo(() => {
        if (!searchQuery)
            return questions;
        const query = searchQuery.toLowerCase();
        return questions.filter((question) => {
            var _a, _b;
            return question.questionText.toLowerCase().includes(query) ||
                question.questionType.toLowerCase().includes(query) ||
                question.frequencyType.toLowerCase().includes(query) ||
                ((_b = (_a = question.section) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(query));
        });
    }, [questions, searchQuery]);
    const columns = [
        {
            label: translate('question_column', { namespace }),
            dataKey: 'questionText',
            renderer: (props) => React.createElement(QuestionRenderer, { item: props.item })
        },
        {
            label: translate('type_column', { namespace }),
            dataKey: 'questionType',
            renderer: (props) => React.createElement(QuestionTypeRenderer, { item: props.item })
        },
        {
            label: translate('frequency_column', { namespace }),
            dataKey: 'frequencyType',
            renderer: (props) => React.createElement(FrequencyRenderer, { item: props.item })
        },
        {
            label: translate('section_column', { namespace }),
            dataKey: 'section',
            renderer: (props) => React.createElement(SectionRenderer, { item: props.item })
        },
        {
            label: '',
            dataKey: 'actions',
            renderer: (props) => (React.createElement(ActionsRenderer, { item: props.item, questionSetId: questionSetId, onRemove: handleRemove, deletingQuestionId: deletingQuestionId }))
        }
    ];
    const sortedQuestions = [...filteredQuestions].sort((a, b) => a.questionText.localeCompare(b.questionText));
    return React.createElement(Table, { columns: columns, data: sortedQuestions, size: TableSize.Full, variant: "detail" });
}
